/**
 * RunLogsPanel — live-tail crawl_logs view used by the JobDetailsDialog
 * Logs tab. Subscribes via useRunLogsLive and renders filtered, colour-coded
 * rows with collapsible metadata.
 *
 * Console-logs filter changes and live toggles so the Chrome DevTools MCP
 * loop can introspect the visible state.
 */
import { useEffect, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Pause,
  Play,
  ScrollText,
  Search,
  X,
} from "lucide-react";
import { Badge } from "@repo/ui/badge";
import { Button } from "@repo/ui/button";
import { Input } from "@repo/ui/input";
import { ScrollArea } from "@repo/ui/scroll-area";
import { cn } from "@repo/ui/lib/utils";
import type { CrawlLogEntry } from "@/lib/api";
import {
  type LogFilters,
  type LogKind,
  type LogLevel,
  useRunLogsLive,
} from "@/hooks/useRunLogsLive";

const LEVEL_OPTIONS: LogLevel[] = ["info", "warn", "error", "debug"];
const KIND_OPTIONS: LogKind[] = [
  "discovery",
  "scrape",
  "checkpoint",
  "provider",
  "seat",
  "trigger",
];

const LEVEL_BADGE_CLASS: Record<LogLevel, string> = {
  info: "bg-sky-500/15 text-sky-700 border-sky-500/30",
  warn: "bg-amber-500/15 text-amber-700 border-amber-500/30",
  error: "bg-red-500/15 text-red-700 border-red-500/30",
  debug: "bg-slate-500/15 text-slate-600 border-slate-500/30",
};

const KIND_BADGE_CLASS: Record<LogKind, string> = {
  discovery: "bg-purple-500/10 text-purple-700 border-purple-500/30",
  scrape: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
  checkpoint: "bg-indigo-500/10 text-indigo-700 border-indigo-500/30",
  provider: "bg-cyan-500/10 text-cyan-700 border-cyan-500/30",
  seat: "bg-fuchsia-500/10 text-fuchsia-700 border-fuchsia-500/30",
  trigger: "bg-orange-500/10 text-orange-700 border-orange-500/30",
};

interface RunLogsPanelProps {
  jobRunId: string | null | undefined;
  adapterOptions?: string[];
  suburbOptions?: string[];
}

export function RunLogsPanel({
  jobRunId,
  adapterOptions = [],
  suburbOptions = [],
}: RunLogsPanelProps) {
  const {
    logs,
    filtered,
    isLoading,
    isLive,
    setLive,
    filters,
    setFilters,
    newSinceLastView,
    resetCounter,
  } = useRunLogsLive(jobRunId);

  // Track live throughput (logs per second over the last 60s) so the user
  // can tell at a glance whether the worker is healthy or stalled.
  const recentTimestamps = useRef<number[]>([]);
  const [perSec, setPerSec] = useState(0);
  useEffect(() => {
    const tick = window.setInterval(() => {
      const now = Date.now();
      recentTimestamps.current = recentTimestamps.current.filter((t) => now - t < 60_000);
      setPerSec(recentTimestamps.current.length / 60);
    }, 1000);
    return () => window.clearInterval(tick);
  }, []);
  useEffect(() => {
    if (logs.length === 0) return;
    recentTimestamps.current.push(Date.now());
  }, [logs.length]);

  const tailEndRef = useRef<HTMLDivElement | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: filtered.length intentionally re-triggers scroll on each new log batch
  useEffect(() => {
    if (isLive) tailEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [filtered.length, isLive]);

  const updateFilters = (next: Partial<LogFilters>) => {
    setFilters({ ...filters, ...next });
    console.log("[RunLogsPanel] filter changed", { ...filters, ...next });
  };

  const toggleLevel = (lvl: LogLevel) => {
    const cur = new Set(filters.levels ?? []);
    if (cur.has(lvl)) cur.delete(lvl);
    else cur.add(lvl);
    updateFilters({ levels: Array.from(cur) });
  };

  const toggleKind = (k: LogKind) => {
    const cur = new Set(filters.kinds ?? []);
    if (cur.has(k)) cur.delete(k);
    else cur.add(k);
    updateFilters({ kinds: Array.from(cur) });
  };

  const clearFilters = () => {
    setFilters({});
    console.log("[RunLogsPanel] filters cleared");
  };

  const activeFilterCount =
    (filters.levels?.length ?? 0) +
    (filters.kinds?.length ?? 0) +
    (filters.adapter ? 1 : 0) +
    (filters.suburb ? 1 : 0) +
    (filters.search ? 1 : 0);

  return (
    <div className="space-y-3">
      {/* Filter bar */}
      <div className="space-y-2 border rounded-md p-3 bg-muted/30">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-muted-foreground">Level:</span>
          {LEVEL_OPTIONS.map((lvl) => (
            <button
              key={lvl}
              type="button"
              onClick={() => toggleLevel(lvl)}
              className={cn(
                "text-xs px-2 py-0.5 rounded border font-mono uppercase tracking-wide",
                filters.levels?.includes(lvl)
                  ? LEVEL_BADGE_CLASS[lvl]
                  : "text-muted-foreground border-muted",
              )}
            >
              {lvl}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-muted-foreground">Kind:</span>
          {KIND_OPTIONS.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => toggleKind(k)}
              className={cn(
                "text-xs px-2 py-0.5 rounded border font-mono",
                filters.kinds?.includes(k)
                  ? KIND_BADGE_CLASS[k]
                  : "text-muted-foreground border-muted",
              )}
            >
              {k}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {adapterOptions.length > 0 && (
            <select
              className="text-xs border rounded px-2 py-1 bg-background"
              value={filters.adapter ?? ""}
              onChange={(e) =>
                updateFilters({ adapter: e.target.value || undefined })
              }
            >
              <option value="">All adapters</option>
              {adapterOptions.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          )}
          {suburbOptions.length > 0 && (
            <select
              className="text-xs border rounded px-2 py-1 bg-background"
              value={filters.suburb ?? ""}
              onChange={(e) =>
                updateFilters({ suburb: e.target.value || undefined })
              }
            >
              <option value="">All suburbs</option>
              {suburbOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          )}
          <div className="flex items-center gap-1 flex-1 min-w-[200px]">
            <Search className="w-3 h-3 text-muted-foreground" />
            <Input
              placeholder="Search messages..."
              value={filters.search ?? ""}
              onChange={(e) =>
                updateFilters({ search: e.target.value || undefined })
              }
              className="h-7 text-xs"
            />
          </div>
          {activeFilterCount > 0 && (
            <Button
              size="sm"
              variant="ghost"
              onClick={clearFilters}
              className="h-7 text-xs"
            >
              <X className="w-3 h-3 mr-1" />
              Clear ({activeFilterCount})
            </Button>
          )}
        </div>
      </div>

      {/* Header strip with throughput + live toggle */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-3">
          <span>
            <span className="font-mono text-foreground">{filtered.length}</span>
            {filters.levels || filters.kinds || filters.adapter || filters.suburb || filters.search ? (
              <span> of {logs.length}</span>
            ) : null}
            {" "}entries
          </span>
          <span>·</span>
          <span>
            <span className="font-mono text-foreground">{perSec.toFixed(1)}</span>
            {" "}logs/sec (last 60s)
          </span>
          {!isLive && newSinceLastView > 0 && (
            <Badge variant="outline" className="text-xs h-5">
              +{newSinceLastView} new
            </Badge>
          )}
        </div>
        <Button
          size="sm"
          variant={isLive ? "default" : "outline"}
          onClick={() => {
            setLive(!isLive);
            if (!isLive) resetCounter();
            console.log("[RunLogsPanel] live toggled", { live: !isLive });
          }}
          className="h-7 text-xs"
        >
          {isLive ? (
            <>
              <Pause className="w-3 h-3 mr-1" />
              Pause live tail
            </>
          ) : (
            <>
              <Play className="w-3 h-3 mr-1" />
              Resume live tail
            </>
          )}
        </Button>
      </div>

      {/* Log list */}
      {isLoading && logs.length === 0 ? (
        <div className="py-8 text-center text-muted-foreground">Loading logs...</div>
      ) : filtered.length === 0 ? (
        <div className="py-8 text-center text-muted-foreground">
          <ScrollText className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p className="font-medium">
            {logs.length === 0 ? "No logs yet" : "No logs match these filters"}
          </p>
          {logs.length === 0 && (
            <p className="text-sm">Worker hasn't emitted any events for this run yet</p>
          )}
        </div>
      ) : (
        <ScrollArea className="max-h-[500px] border rounded-md">
          <div className="divide-y">
            {filtered.map((log) => (
              <LogRow key={log.id} log={log} />
            ))}
            <div ref={tailEndRef} />
          </div>
        </ScrollArea>
      )}
    </div>
  );
}

function LogRow({ log }: { log: CrawlLogEntry }) {
  const [expanded, setExpanded] = useState(false);
  const metadata = (log.metadata ?? {}) as Record<string, unknown>;
  const kind = (metadata.kind as LogKind | undefined) ?? null;
  const adapter = metadata.adapter as string | undefined;
  const suburb = metadata.suburb as string | undefined;
  const hasMetadata = Object.keys(metadata).length > 0;

  return (
    <div
      className={cn(
        "px-3 py-1.5 hover:bg-muted/40 transition-colors text-xs font-mono",
        log.level === "error" && "bg-red-500/5",
        log.level === "warn" && "bg-amber-500/5",
      )}
    >
      <div className="flex items-start gap-2">
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground"
          onClick={() => setExpanded(!expanded)}
          disabled={!hasMetadata}
        >
          {hasMetadata ? (
            expanded ? (
              <ChevronDown className="w-3 h-3" />
            ) : (
              <ChevronRight className="w-3 h-3" />
            )
          ) : (
            <span className="inline-block w-3 h-3" />
          )}
        </button>
        <span className="text-muted-foreground whitespace-nowrap w-[78px]">
          {new Date(log.created_at).toLocaleTimeString("en-AU", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
          })}
        </span>
        <Badge
          variant="outline"
          className={cn("text-[10px] px-1 py-0 uppercase", LEVEL_BADGE_CLASS[log.level as LogLevel])}
        >
          {log.level}
        </Badge>
        {kind && (
          <Badge
            variant="outline"
            className={cn("text-[10px] px-1 py-0", KIND_BADGE_CLASS[kind])}
          >
            {kind}
          </Badge>
        )}
        {adapter && (
          <span className="text-muted-foreground whitespace-nowrap">{adapter}</span>
        )}
        {suburb && (
          <span className="text-muted-foreground whitespace-nowrap">/{suburb}</span>
        )}
        <span className="flex-1 break-all text-foreground">{log.message}</span>
      </div>
      {expanded && hasMetadata && (
        <pre className="ml-8 mt-1 text-[10px] text-muted-foreground bg-muted/30 p-2 rounded overflow-x-auto">
          {JSON.stringify(metadata, null, 2)}
        </pre>
      )}
    </div>
  );
}
